import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { fetchFaqs, searchFaq } from "../../services/faqService";
import { FaqEntry, FaqSearchMatch } from "../../types";

interface ChatMessage {
  id: string;
  sender: "user" | "bot";
  content: string;
  timestamp: string;
  isError?: boolean;
}

const createMessage = (
  sender: "user" | "bot",
  content: string,
  isError = false
): ChatMessage => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sender,
  content,
  timestamp: new Date().toISOString(),
  isError,
});

const MAX_QUICK_QUESTIONS = 4;

const FaqChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasWelcomed, setHasWelcomed] = useState(false);
  const [faqs, setFaqs] = useState<FaqEntry[]>([]);
  const [quickQuestions, setQuickQuestions] = useState<FaqEntry[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen && !hasWelcomed) {
      setMessages([
        createMessage(
          "bot",
          "Hi there! I'm your Swags29 assistant. Ask me anything about classes, plans, or the platform, and I'll point you to the right answer."
        ),
      ]);
      setHasWelcomed(true);
    }
  }, [isOpen, hasWelcomed]);

  useEffect(() => {
    if (isOpen && faqs.length === 0) {
      fetchFaqs()
        .then((items) => {
          setFaqs(items);
          setQuickQuestions(items.slice(0, MAX_QUICK_QUESTIONS));
        })
        .catch(() => {
          setMessages((prev) => [
            ...prev,
            createMessage(
              "bot",
              "I'm having trouble loading our FAQs right now. You can still type your question and I'll do my best to help!",
              true
            ),
          ]);
        });
    }
  }, [isOpen, faqs.length]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  const handleSearchResponse = useCallback(
    (match: FaqSearchMatch | null, suggestions: FaqEntry[], query: string) => {
      if (match) {
        const botReply = `Here's what I found for "${match.faq.question}":\n\n${match.faq.answer}`;
        setMessages((prev) => [...prev, createMessage("bot", botReply)]);
      } else {
        // Show a simple "not found" message without mentioning suggestions
        const noMatchMessage =
          "I couldn't find a specific answer to that question. You can try asking something else or browse the questions below.";
        setMessages((prev) => [...prev, createMessage("bot", noMatchMessage)]);
      }

      if (suggestions.length > 0) {
        const trimmedSuggestions = suggestions.slice(0, MAX_QUICK_QUESTIONS);
        setQuickQuestions(trimmedSuggestions);
      } else if (faqs.length > 0) {
        setQuickQuestions(faqs.slice(0, MAX_QUICK_QUESTIONS));
      }
    },
    [faqs]
  );

  const handleSend = useCallback(
    async (overrideQuestion?: string) => {
      if (isLoading) {
        return;
      }

      const rawQuestion =
        overrideQuestion !== undefined ? overrideQuestion : inputValue;
      const question = rawQuestion.trim();

      if (!question) {
        return;
      }

      setMessages((prev) => [...prev, createMessage("user", question)]);
      setInputValue("");
      setIsLoading(true);

      try {
        const result = await searchFaq(question);
        handleSearchResponse(result.match, result.suggestions, result.query);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.";

        setMessages((prev) => [
          ...prev,
          createMessage("bot", errorMessage, true),
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [handleSearchResponse, inputValue, isLoading]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const statusText = useMemo(
    () => (isLoading ? "Thinking..." : "Online"),
    [isLoading]
  );

  const toggleChatbot = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleQuickQuestion = useCallback(
    (question: string) => {
      handleSend(question);
    },
    [handleSend]
  );

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
      <button
        type="button"
        onClick={toggleChatbot}
        aria-label={isOpen ? "Close FAQ assistant" : "Open FAQ assistant"}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl transition-all duration-300 hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-400/60"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {isOpen && (
        <div className="mt-3 w-[min(90vw,22rem)] sm:w-80 md:w-96 rounded-3xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Swags29 FAQ Assistant
              </p>
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex h-2 w-2 items-center justify-center">
                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                </span>
                <span>{statusText}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-300">
              <Sparkles size={16} />
              <span className="font-medium">FAQ powered</span>
            </div>
          </div>

          {quickQuestions.length > 0 && (
            <div className="px-4 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Quick Questions
              </p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((faq) => (
                  <button
                    key={faq.id}
                    type="button"
                    className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-200 dark:hover:bg-blue-500/20"
                    onClick={() => handleQuickQuestion(faq.question)}
                  >
                    {faq.question}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="custom-scrollbar mt-3 max-h-64 overflow-y-auto px-4 pb-4">
            <div className="space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-md sm:max-w-[80%] ${
                      message.sender === "user"
                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                        : message.isError
                        ? "bg-red-500 text-white dark:bg-red-600"
                        : "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                    }`}
                  >
                    <p className="whitespace-pre-line leading-relaxed">
                      {message.content}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl bg-gray-100 px-4 py-2 text-sm text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Searching knowledge base...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="border-t border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <textarea
                rows={2}
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about classes, plans, or support..."
                className="h-20 w-full resize-none rounded-2xl bg-transparent px-3 pt-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none dark:text-gray-100 dark:placeholder-gray-500"
                aria-label="Type your question"
                disabled={isLoading}
              />
              <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2 dark:border-gray-700">
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Press Enter to send • Shift + Enter for a new line
                </span>
                <button
                  type="button"
                  onClick={() => handleSend()}
                  disabled={isLoading || inputValue.trim().length === 0}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  <Send size={16} />
                  Ask
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqChatbot;
